import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, AlertCircle, Factory, Truck, Plus, Minus, Package } from 'lucide-react';
import salesService from '../services/salesService';
import purchaseService from '../services/purchaseService';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import BomForm from './BomForm';

// Reusing SupplierAutocomplete from SoPoSplittingDialog logic
const SupplierAutocomplete = ({ suppliers, value, onChange, placeholder, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedSupplier = suppliers.find(s => (s._id || s.supplier_id) === value);

  useEffect(() => {
    if (selectedSupplier) {
      setInputValue(selectedSupplier.supplier_name || '');
    }
  }, [selectedSupplier]);

  const filteredSuppliers = suppliers.filter(s =>
    s.supplier_name?.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative w-full">
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
      />
      {isOpen && filteredSuppliers.length > 0 && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredSuppliers.map((supplier) => (
            <div
              key={supplier._id || supplier.supplier_id}
              className="px-3 py-2 cursor-pointer hover:bg-muted text-sm"
              onMouseDown={() => {
                onChange(supplier._id || supplier.supplier_id);
                setInputValue(supplier.supplier_name);
                setIsOpen(false);
              }}
            >
              {supplier.supplier_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UnifiedConversionDialog = ({ open, onClose, salesOrderId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [allocations, setAllocations] = useState({}); // { [line_item_id]: { purchase_qty, supplier_id, production_qty, bom_id } }
  const [suppliers, setSuppliers] = useState([]);
  
  // Stock Allocation State
  const [stockAllocations, setStockAllocations] = useState({});
  const [stockPickerOpen, setStockPickerOpen] = useState(false);
  const [stockPickerProduct, setStockPickerProduct] = useState(null);
  
  // BOM Creation Drawer State
  const [bomSheetOpen, setBomSheetOpen] = useState(false);
  const [bomTargetProduct, setBomTargetProduct] = useState(null);

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setAnalyzing(true);
      setError(null);

      // Parallel fetch: Analysis + Suppliers
      const [analysisRes, suppliersRes] = await Promise.all([
        salesService.getShortageAnalysis(salesOrderId),
        purchaseService.getSuppliers(1, 100)
      ]);

      setAnalysisData(analysisRes);
      
      const supplierList = Array.isArray(suppliersRes) 
        ? suppliersRes 
        : (suppliersRes.suppliers || []);
      setSuppliers(supplierList);

      // Initialize allocations based on shortage
      const initialAllocations = {};
      analysisRes.products.forEach(p => {
        if (p.shortage > 0) {
          initialAllocations[p.line_item_id] = {
            purchase_qty: 0,
            supplier_id: '',
            production_qty: 0,
            bom_id: p.available_boms && p.available_boms.length > 0 ? p.available_boms[0].bom_id : '',
            unit_price: 0 // Should fetch this from price history if possible, simplified for now
          };
          
          // Auto-allocate logic: if can manufacture, default to production, else default to purchase
          if (p.can_manufacture) {
            initialAllocations[p.line_item_id].production_qty = p.shortage;
          } else {
            initialAllocations[p.line_item_id].purchase_qty = p.shortage;
          }
        }
      });
      setAllocations(initialAllocations);

    } catch (err) {
      console.error("Failed to load analysis:", err);
      setError("加载缺货分析失败: " + (err.response?.data?.error || err.message));
    } finally {
      setAnalyzing(false);
    }
  }, [salesOrderId]);

  useEffect(() => {
    if (open && salesOrderId) {
      loadData();
    }
  }, [open, salesOrderId, loadData]);

  const handleAllocationChange = (lineItemId, field, value) => {
    setAllocations(prev => ({
      ...prev,
      [lineItemId]: {
        ...prev[lineItemId],
        [field]: value
      }
    }));
  };

  const handleOpenStockPicker = (product) => {
    setStockPickerProduct(product);
    setStockPickerOpen(true);
  };

  const handleStockSelect = (lineItemId, selectedStock, keepOpen = false) => {
    setStockAllocations(prev => ({
      ...prev,
      [lineItemId]: selectedStock
    }));
    if (!keepOpen) {
      setStockPickerOpen(false);
      setStockPickerProduct(null);
    }
  };

  const getTotalStockAllocated = (lineItemId) => {
    const allocations = stockAllocations[lineItemId] || [];
    return allocations.reduce((sum, alloc) => sum + (Number(alloc.quantity) || 0), 0);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Construct payload
      const payload = {
        allocations: Object.entries(allocations)
          .map(([lineItemId, alloc]) => {
             const product = analysisData.products.find(p => p.line_item_id === lineItemId);
             if (!product) return null;
             
             // Only include if there is quantity allocated
             if ((Number(alloc.purchase_qty) || 0) <= 0 && (Number(alloc.production_qty) || 0) <= 0) {
               return null;
             }

             return {
               line_item_id: lineItemId,
               product_id: product.product_id,
               product_code: product.product_code,
               shortage_qty: product.shortage,
               purchase_qty: Number(alloc.purchase_qty) || 0,
               supplier_id: alloc.supplier_id,
               supplier_name: suppliers.find(s => s._id === alloc.supplier_id)?.supplier_name || '',
               unit_price: Number(alloc.unit_price) || 0,
               production_qty: Number(alloc.production_qty) || 0,
               bom_id: alloc.bom_id
             };
          })
          .filter(Boolean)
      };

      // Build stock_allocations from state
      const stockAllocationsList = Object.entries(stockAllocations)
        .flatMap(([lineItemId, allocs]) => {
          const product = analysisData.products.find(p => p.line_item_id === lineItemId);
          if (!product) return [];
          return allocs
            .filter(alloc => (Number(alloc.quantity) || 0) > 0)
            .map(alloc => ({
              line_item_id: lineItemId,
              product_code: product.product_code,
              product_name: product.product_name,
              location: alloc.location,
              quantity: Number(alloc.quantity),
              batch_number: alloc.batch_number || '',
              source_client_ref: alloc.source_client_ref,
              source_client_name: alloc.source_client_name
            }));
        });

      payload.stock_allocations = stockAllocationsList;

      // Check if we have any allocations at all
      const hasAllocations = payload.allocations.length > 0 || stockAllocationsList.length > 0;
      if (!hasAllocations) {
        setError("请至少分配一个产品的数量（采购、生产或库存）");
        setLoading(false);
        return;
      }

      // Validate suppliers for purchase
      const missingSupplier = payload.allocations.find(a => a.purchase_qty > 0 && !a.supplier_id);
      if (missingSupplier) {
        setError(`产品 ${missingSupplier.product_code} 设置了采购数量，但未选择供应商`);
        setLoading(false);
        return;
      }

      // Validate BOMs for production
      const missingBom = payload.allocations.find(a => a.production_qty > 0 && !a.bom_id);
      if (missingBom) {
        setError(`产品 ${missingBom.product_code} 设置了生产数量，但未选择BOM`);
        setLoading(false);
        return;
      }

      const result = await salesService.unifiedConversion(salesOrderId, payload);
      
      if (onSuccess) onSuccess(result);
      onClose();

    } catch (err) {
      console.error("Conversion failed:", err);
      setError("转换失败: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>生成供应单 (采购/生产)</DialogTitle>
          <DialogDescription>
            根据缺货情况，一站式生成采购订单和生产工单。
          </DialogDescription>
        </DialogHeader>

        {analyzing ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">正在分析缺货情况...</p>
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
            <Button variant="outline" size="sm" className="mt-2" onClick={loadData}>
              重试
            </Button>
          </Alert>
        ) : (
          <div className="space-y-4">
            {analysisData?.summary && (
              <div className="flex gap-4 p-4 bg-muted rounded-lg text-sm">
                <div>
                  <span className="text-muted-foreground">总产品数:</span>
                  <span className="ml-2 font-medium">{analysisData.summary.total_products}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">缺货产品:</span>
                  <span className="ml-2 font-medium text-destructive">{analysisData.summary.products_with_shortage}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">可自制:</span>
                  <span className="ml-2 font-medium text-primary">{analysisData.summary.products_can_manufacture}</span>
                </div>
              </div>
            )}

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">产品</TableHead>
                    <TableHead className="text-right w-[100px]">订单数</TableHead>
                    <TableHead className="text-right w-[100px]">当前库存</TableHead>
                    <TableHead className="text-right w-[100px]">已分配</TableHead>
                    <TableHead className="text-right w-[100px] text-destructive font-bold">缺货量</TableHead>
                    <TableHead className="w-[200px] bg-amber-50/50">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-amber-600" />
                        库存分配
                      </div>
                    </TableHead>
                    <TableHead className="w-[300px] bg-blue-50/50">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-blue-600" />
                        采购分配
                      </div>
                    </TableHead>
                    <TableHead className="w-[300px] bg-green-50/50">
                      <div className="flex items-center gap-2">
                        <Factory className="h-4 w-4 text-green-600" />
                        生产分配
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analysisData?.products.map((product) => {
                    const alloc = allocations[product.line_item_id] || {};
                    const isShortage = product.shortage > 0;
                    const hasStock = product.current_stock > 0;

                    return (
                      <TableRow key={product.line_item_id} className={!isShortage ? "bg-muted/10" : ""}>
                        <TableCell>
                          <div className="font-medium">{product.product_code}</div>
                          <div className="text-xs text-muted-foreground">{product.product_name}</div>
                        </TableCell>
                        <TableCell className="text-right">{product.ordered_qty}</TableCell>
                        <TableCell className="text-right">{product.current_stock}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {product.po_allocated + product.production_allocated + (product.frozen_allocated || 0)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-destructive">
                          {product.shortage > 0 ? product.shortage : '-'}
                        </TableCell>
                        
                        {/* Stock Allocation Column */}
                        <TableCell className="bg-amber-50/30 p-2 align-top">
                          {product.available_stock_details?.length > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">
                                  已选: {getTotalStockAllocated(product.line_item_id)} 件
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => handleOpenStockPicker(product)}
                                  disabled={!hasStock}
                                >
                                  选择库存
                                </Button>
                              </div>
                              {stockAllocations[product.line_item_id]?.length > 0 && (
                                <div className="text-xs space-y-1">
                                  {stockAllocations[product.line_item_id].map((alloc, idx) => (
                                    <div key={idx} className="flex justify-between text-muted-foreground">
                                      <span>{alloc.location}</span>
                                      <span>{alloc.quantity} 件</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground text-center py-2">
                              无可用库存
                            </div>
                          )}
                        </TableCell>

                        {/* Purchase Column */}
                        <TableCell className="bg-blue-50/30 p-2 align-top">
                             <div className="space-y-2">
                               <div className="flex items-center gap-2">
                                 <span className="text-xs w-10 text-muted-foreground">数量</span>
                                 <Input 
                                   type="number" 
                                   className="h-8" 
                                   value={alloc.purchase_qty || ''}
                                   onChange={(e) => handleAllocationChange(product.line_item_id, 'purchase_qty', e.target.value)}
                                 />
                               </div>
                               <div className="flex items-center gap-2">
                                 <span className="text-xs w-10 text-muted-foreground">供应商</span>
                                 <SupplierAutocomplete 
                                   suppliers={suppliers}
                                   value={alloc.supplier_id}
                                   onChange={(val) => handleAllocationChange(product.line_item_id, 'supplier_id', val)}
                                   placeholder="选择供应商"
                                 />
                               </div>
                               <div className="flex items-center gap-2">
                                 <span className="text-xs w-10 text-muted-foreground">单价</span>
                                 <Input 
                                   type="number" 
                                   className="h-8" 
                                   placeholder="预估单价"
                                   value={alloc.unit_price || ''}
                                   onChange={(e) => handleAllocationChange(product.line_item_id, 'unit_price', e.target.value)}
                                 />
                               </div>
                           </div>
                        </TableCell>

                        {/* Production Column */}
                        <TableCell className="bg-green-50/30 p-2 align-top">
                          {product.can_manufacture ? (
                             <div className="space-y-2">
                               <div className="flex items-center gap-2">
                                 <span className="text-xs w-10 text-muted-foreground">数量</span>
                                 <Input 
                                   type="number" 
                                   className="h-8"
                                   value={alloc.production_qty || ''}
                                   onChange={(e) => handleAllocationChange(product.line_item_id, 'production_qty', e.target.value)}
                                 />
                               </div>
                               <div className="flex items-center gap-2">
                                 <span className="text-xs w-10 text-muted-foreground">BOM</span>
                                 <Select 
                                   value={alloc.bom_id} 
                                   onValueChange={(val) => handleAllocationChange(product.line_item_id, 'bom_id', val)}
                                 >
                                   <SelectTrigger className="h-8">
                                     <SelectValue placeholder="选择BOM" />
                                   </SelectTrigger>
                                   <SelectContent>
                                     {product.available_boms.map(bom => (
                                       <SelectItem key={bom.bom_id} value={bom.bom_id}>
                                         {bom.bom_code} (v{bom.version})
                                       </SelectItem>
                                     ))}
                                   </SelectContent>
                                 </Select>
                               </div>
                             </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2 py-2">
                              <div className="text-xs text-muted-foreground text-center">
                                无可用BOM
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 text-xs border-dashed"
                                onClick={() => {
                                  setBomTargetProduct(product);
                                  setBomSheetOpen(true);
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                创建BOM
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>取消</Button>
          <Button onClick={handleSubmit} disabled={loading || analyzing || !analysisData}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            生成订单
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={stockPickerOpen} onOpenChange={setStockPickerOpen}>
        <DialogContent 
          className="max-w-3xl max-h-[80vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              选择库存 - {stockPickerProduct?.product_code}
            </DialogTitle>
            <DialogDescription>
              选择要冻结给此订单的库存位置和数量
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-muted-foreground">订单数量</div>
                  <div className="text-lg font-semibold">{stockPickerProduct?.ordered_qty} 件</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">还可分配</div>
                  <div className={`text-lg font-semibold ${
                    (stockPickerProduct?.ordered_qty || 0) - getTotalStockAllocated(stockPickerProduct?.line_item_id) <= 0
                      ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {Math.max(0, (stockPickerProduct?.ordered_qty || 0) - getTotalStockAllocated(stockPickerProduct?.line_item_id))} 件
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">已选择</div>
                  <div className="text-lg font-semibold text-amber-600">
                    {getTotalStockAllocated(stockPickerProduct?.line_item_id)} 件
                  </div>
                </div>
              </div>
              {(stockPickerProduct?.ordered_qty || 0) > 0 && (
                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        getTotalStockAllocated(stockPickerProduct?.line_item_id) > (stockPickerProduct?.ordered_qty || 0)
                          ? 'bg-red-500'
                          : getTotalStockAllocated(stockPickerProduct?.line_item_id) === (stockPickerProduct?.ordered_qty || 0)
                          ? 'bg-green-500'
                          : 'bg-amber-500'
                      }`}
                      style={{
                        width: `${Math.min(100, (getTotalStockAllocated(stockPickerProduct?.line_item_id) / (stockPickerProduct?.ordered_qty || 1)) * 100)}%`
                      }}
                    />
                  </div>
                  {getTotalStockAllocated(stockPickerProduct?.line_item_id) > (stockPickerProduct?.ordered_qty || 0) && (
                    <div className="text-xs text-red-600 mt-1 text-center">
                      警告：已超出订单需求数量
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3 font-medium">库位</th>
                    <th className="text-left p-3 font-medium">批次</th>
                    <th className="text-left p-3 font-medium">所属客户</th>
                    <th className="text-right p-3 font-medium">可用库存</th>
                    <th className="text-center p-3 font-medium w-32">分配数量</th>
                  </tr>
                </thead>
                <tbody>
                  {stockPickerProduct?.available_stock_details?.map((stock, idx) => {
                    const currentAlloc = stockAllocations[stockPickerProduct?.line_item_id]?.find(
                      a => a.location === stock.location && a.batch_number === stock.batch_number && a.source_client_ref === stock.client_ref
                    );
                    const currentQty = currentAlloc?.quantity || 0;
                    const totalAllocated = getTotalStockAllocated(stockPickerProduct?.line_item_id);
                    const otherAllocated = totalAllocated - currentQty;
                    const orderQty = stockPickerProduct?.ordered_qty || 0;
                    const remainingAllowed = Math.max(0, orderQty - otherAllocated);
                    const isAllocated = currentQty > 0;
                    
                    return (
                      <tr key={idx} className={`border-t hover:bg-muted/50 transition-colors ${isAllocated ? 'bg-amber-50/50' : ''}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${isAllocated ? 'bg-amber-500' : 'bg-green-500'}`}></div>
                            <span className="font-medium">{stock.location}</span>
                            {isAllocated && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">
                                已分配
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {stock.batch_number || '-'}
                        </td>
                        <td className="p-3">
                          <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {stock.client_name || '未知客户'}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-semibold text-green-600">{stock.remaining_quantity}</span>
                          <span className="text-sm text-muted-foreground"> 件</span>
                        </td>
                        <td className="p-3">
                          <div className={`flex items-center justify-center gap-2 p-1.5 rounded-lg transition-all ${isAllocated ? 'bg-amber-100 ring-2 ring-amber-300' : ''}`}>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newQty = Math.max(0, currentQty - 1);
                                const existingAllocs = stockAllocations[stockPickerProduct?.line_item_id] || [];
                                const otherAllocs = existingAllocs.filter(
                                  a => !(a.location === stock.location && a.batch_number === stock.batch_number && a.source_client_ref === stock.client_ref)
                                );

                                if (newQty > 0) {
                                  handleStockSelect(stockPickerProduct?.line_item_id, [
                                    ...otherAllocs,
                                    {
                                      location: stock.location,
                                      batch_number: stock.batch_number,
                                      quantity: newQty,
                                      source_client_ref: stock.client_ref,
                                      source_client_name: stock.client_name
                                    }
                                  ], true);
                                } else {
                                  handleStockSelect(stockPickerProduct?.line_item_id, otherAllocs, true);
                                }
                              }}
                              disabled={currentQty <= 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            
                            <Input
                              type="number"
                              className={`w-16 text-center h-8 font-semibold transition-all ${
                                currentQty > 0 
                                  ? 'bg-amber-50 border-amber-400 text-amber-900 ring-1 ring-amber-300' 
                                  : 'bg-white'
                              }`}
                              min={0}
                              max={stock.remaining_quantity}
                              value={currentQty || ''}
                              onChange={(e) => {
                                const qty = parseFloat(e.target.value) || 0;
                                const validQty = Math.min(qty, stock.remaining_quantity, remainingAllowed);
                                const existingAllocs = stockAllocations[stockPickerProduct?.line_item_id] || [];
                                const otherAllocs = existingAllocs.filter(
                                  a => !(a.location === stock.location && a.batch_number === stock.batch_number && a.source_client_ref === stock.client_ref)
                                );
                                
                                if (validQty > 0) {
                                  handleStockSelect(stockPickerProduct?.line_item_id, [
                                    ...otherAllocs,
                                    { 
                                      location: stock.location, 
                                      batch_number: stock.batch_number, 
                                      quantity: validQty,
                                      source_client_ref: stock.client_ref,
                                      source_client_name: stock.client_name
                                    }
                                  ], true);
                                } else {
                                  handleStockSelect(stockPickerProduct?.line_item_id, otherAllocs, true);
                                }
                              }}
                            />
                            
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newQty = Math.min(stock.remaining_quantity, currentQty + 1, remainingAllowed);
                                const existingAllocs = stockAllocations[stockPickerProduct?.line_item_id] || [];
                                const otherAllocs = existingAllocs.filter(
                                  a => !(a.location === stock.location && a.batch_number === stock.batch_number && a.source_client_ref === stock.client_ref)
                                );

                                handleStockSelect(stockPickerProduct?.line_item_id, [
                                  ...otherAllocs,
                                  {
                                    location: stock.location,
                                    batch_number: stock.batch_number,
                                    quantity: newQty,
                                    source_client_ref: stock.client_ref,
                                    source_client_name: stock.client_name
                                  }
                                ], true);
                              }}
                              disabled={currentQty >= stock.remaining_quantity || currentQty >= remainingAllowed}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  let remainingToAllocate = stockPickerProduct?.ordered_qty || 0;
                  const newAllocs = [];

                  for (const stock of stockPickerProduct?.available_stock_details || []) {
                    if (remainingToAllocate <= 0) break;
                    const allocQty = Math.min(remainingToAllocate, stock.remaining_quantity);
                    if (allocQty > 0) {
                      newAllocs.push({
                        location: stock.location,
                        batch_number: stock.batch_number,
                        quantity: allocQty
                      });
                      remainingToAllocate -= allocQty;
                    }
                  }

                  handleStockSelect(stockPickerProduct?.line_item_id, newAllocs, true);
                }}
                disabled={!stockPickerProduct?.available_stock_details?.length}
              >
                自动分配
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStockSelect(stockPickerProduct?.line_item_id, [], true);
                }}
              >
                清空选择
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockPickerOpen(false)}>
              完成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={bomSheetOpen} onOpenChange={setBomSheetOpen}>
        <SheetContent side="right" className="w-[90vw] max-w-5xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>快速创建BOM</SheetTitle>
            <SheetDescription>
              为产品 {bomTargetProduct?.product_code} 创建物料清单。
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
             {bomTargetProduct && (
               <BomForm 
                 finishedProduct={bomTargetProduct}
                 onSuccess={() => {
                   setBomSheetOpen(false);
                   loadData();
                 }}
                 onCancel={() => setBomSheetOpen(false)}
               />
             )}
          </div>
        </SheetContent>
      </Sheet>
    </Dialog>
  );
};

export default UnifiedConversionDialog;
